import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { connect } from 'react-redux';
import { hasChatPermission } from '../domestic/header/headerProfileGroups';
import { useChatWidget } from '../../contexts/ChatWidgetContext';
import ChatLauncherButton from './ChatLauncherButton';
import { trackPanelOpened } from './chatAnalytics';
import '../../pages/css/domesticTheme.css';
import styles from './css/ChatWidgetNudge.module.css';

const ChatPanel = lazy(() => import('./ChatPanel'));

const NUDGE_DELAY_MS = 3000;
const NUDGE_AUTO_DISMISS_MS = 5000;
const STORAGE_KEY_NUDGE_SEEN = 'chat_nudge_seen';

function ChatWidget({ isLoggedIn, showChat }) {
  const [showNudge, setShowNudge] = useState(false);
  const nudgeTimerRef = useRef(null);
  const dismissTimerRef = useRef(null);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY_NUDGE_SEEN)) return;
    } catch {}
    nudgeTimerRef.current = setTimeout(() => {
      setShowNudge(true);
      dismissTimerRef.current = setTimeout(() => {
        setShowNudge(false);
        try {
          sessionStorage.setItem(STORAGE_KEY_NUDGE_SEEN, '1');
        } catch {}
      }, NUDGE_AUTO_DISMISS_MS);
    }, NUDGE_DELAY_MS);
    return () => {
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const handlePanelOpen = () => {
    setShowNudge(false);
    try {
      sessionStorage.setItem(STORAGE_KEY_NUDGE_SEEN, '1');
    } catch {}
  };

  if (!isLoggedIn || !showChat) return null;

  return (
    <>
      <ChatLauncherButton />
      <ChatWidgetPanelWithNudge
        showNudge={showNudge}
        onNudgeDismiss={() => {
          setShowNudge(false);
          try {
            sessionStorage.setItem(STORAGE_KEY_NUDGE_SEEN, '1');
          } catch {}
        }}
        onPanelOpen={handlePanelOpen}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  isLoggedIn: Boolean(state.user?.username && state.user?.idToken),
  showChat: hasChatPermission(state.user, state.assumeuser),
});

export default connect(mapStateToProps)(ChatWidget);

function ChatWidgetPanelWithNudge({ showNudge, onNudgeDismiss, onPanelOpen }) {
  const { panelState, openSource } = useChatWidget();
  const prevStateRef = useRef(panelState);
  const hasMountedPanelRef = useRef(false);

  useEffect(() => {
    if (prevStateRef.current !== 'open' && panelState === 'open') {
      trackPanelOpened(openSource);
      onPanelOpen?.();
    }
    prevStateRef.current = panelState;
  }, [panelState, openSource, onPanelOpen]);

  if (panelState === 'open' || panelState === 'minimized') {
    hasMountedPanelRef.current = true;
  } else if (panelState === 'closed') {
    hasMountedPanelRef.current = false;
  }

  const shouldRenderPanel = hasMountedPanelRef.current;
  const isMinimized = panelState === 'minimized';

  return (
    <>
      {showNudge && (
        <div
          className={`${styles.nudge} domestic-design-scope`}
          role="tooltip"
          aria-live="polite"
        >
          <span className={styles.nudgeText}>Questions? Ask the CargoTrack Assistant</span>
          <button
            type="button"
            className={styles.nudgeClose}
            onClick={onNudgeDismiss}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {shouldRenderPanel && (
        <Suspense fallback={null}>
          <ChatPanel isMinimized={isMinimized} />
        </Suspense>
      )}
    </>
  );
}

